import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Reports from audio → Combine recordings. Fixture `combineRecordings`:
// the officer adds the dictation (narrative-retail-theft.mp3) and then the
// witness interview (interaction-witness-interview.mp3), sets the interview to
// Interaction with two speakers, drags the dictation below it, and uploads;
// the one finished report uses both. Rows are indexed by `data-file-index`.
const ROW_0 = '[data-file-index="0"]';
const ROW_1 = '[data-file-index="1"]';
const SPEAKER_1 = `${ROW_1} .space-y-2 > div:nth-child(1)`;
const SPEAKER_2 = `${ROW_1} .space-y-2 > div:nth-child(2)`;

const scene: Scene = {
  id: "combine-narrative-and-interaction",
  title: "Combine recordings",
  startRoute: "/cases",
  fixture: "combineRecordings",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "The strongest reports usually come from two recordings: the interview itself, and a short narrative where you dictate what you observed, the timeline, and the disposition. One case can hold up to ten files, and the AI reads them all.",
      actions: [
        { type: "waitFor", target: sel("newCaseDropdown") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "add-files",
      narration:
        "I'll click New Case, choose Upload Files, and add the recordings. First my dictation, then the witness interview.",
      actions: [
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 300 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
        { type: "moveTo", target: sel("uploadDropzone") },
        { type: "wait", ms: 1500 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/narrative-retail-theft.mp3`,
          name: "narrative-retail-theft.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: ROW_0 },
        { type: "wait", ms: 1700 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/interaction-witness-interview.mp3`,
          name: "interaction-witness-interview.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: ROW_1 },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "set-types",
      narration:
        "Set each row to Narrative or Interaction. The dictation is a narrative, so I'll leave it. The interview is an interaction, so I'll switch that row.",
      actions: [
        { type: "moveTo", target: `${ROW_0} ${sel("uploadTypeNarrative")}` },
        { type: "spotlight", target: `${ROW_0} ${sel("uploadTypeNarrative")}` },
        // "I'll switch that row" is spoken at about 8 s of 9.3 s.
        { type: "wait", ms: 6600 },
        { type: "spotlight", target: null },
        { type: "click", target: `${ROW_1} ${sel("uploadTypeInteraction")}` },
        { type: "waitFor", target: `${ROW_1} ${sel("uploadAddSpeaker")}` },
        { type: "wait", ms: 500 },
      ],
    },
    {
      id: "speakers",
      narration:
        "On the interaction row, I add the speakers: myself as the officer, and Daniel Reyes as the witness.",
      actions: [
        { type: "click", target: `${ROW_1} ${sel("uploadAddSpeaker")}` },
        { type: "waitFor", target: `${SPEAKER_1} input` },
        { type: "click", target: `${ROW_1} ${sel("uploadAddSpeaker")}` },
        { type: "waitFor", target: `${SPEAKER_2} input` },
        { type: "wait", ms: 700 },
        { type: "type", target: `${SPEAKER_1} input`, text: "Officer Ramirez", cps: 20 },
        { type: "selectOption", target: `${SPEAKER_1} select`, value: "officer" },
        { type: "type", target: `${SPEAKER_2} input`, text: "Daniel Reyes", cps: 20 },
        { type: "selectOption", target: `${SPEAKER_2} select`, value: "witness" },
        { type: "wait", ms: 500 },
      ],
    },
    {
      id: "reorder",
      narration:
        "Now put the rows in the order things happened. The interview came first and your dictation usually goes last, so I'll drag the dictation to the bottom.",
      actions: [
        { type: "scrollIntoView", target: ROW_0 },
        // "so I'll drag the dictation" is spoken at about 6.4 s of 8.6 s.
        { type: "wait", ms: 5200 },
        { type: "dragTo", target: ROW_0, to: ROW_1 },
        { type: "wait", ms: 1000 },
      ],
    },
    {
      id: "settings",
      narration:
        "Open Report Settings if you want a template or a different detail level. Then the button shows how many files you're uploading.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadReportSettings") },
        { type: "click", target: sel("uploadReportSettings") },
        { type: "wait", ms: 2500 },
        { type: "click", target: sel("uploadReportSettings") },
        { type: "wait", ms: 400 },
        { type: "scrollIntoView", target: sel("uploadSubmit") },
        { type: "moveTo", target: sel("uploadSubmit") },
        { type: "spotlight", target: sel("uploadSubmit") },
        { type: "wait", ms: 1500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "upload",
      narration:
        "Each file is transcribed on its own, and the draft starts once every file has finished. Stay on the page until the upload completes, then you can leave.",
      actions: [
        { type: "click", target: sel("uploadSubmit") },
        { type: "waitFor", target: sel("editorProcessing"), timeoutMs: 20000 },
        { type: "wait", ms: 4000 },
        { type: "fixture", name: "completeLatestCase" },
      ],
    },
    {
      id: "draft",
      narration:
        "And here's one report. The quotes come from the interview, and the timeline and the disposition come from my dictation. That's why the narrative should cover what an interview never captures: times, what you saw, and the outcome.",
      actions: [
        { type: "waitFor", target: sel("reportScore"), timeoutMs: 15000 },
        { type: "wait", ms: 2500 },
        { type: "moveTo", target: sel("editorNarrative") },
        { type: "spotlight", target: sel("editorNarrative") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
